import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import {
  normalizeDashboardTabs,
  normalizeTabEntry,
  processMarketApps
} from '@/features/Dashboard/lib/transformers';
import type { DashboardResponse, DashboardTab } from '@/features/Dashboard/types';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { getDashboardApiUrl } from './dashboardUrl';

// Query Keys helper – colocated with feature API
export const dashboardQueryKeys = {
  all: ['dashboard'] as const
};

export const useDashboard = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.all,
    queryFn: async () => {
      const { data } =
        await httpInstances.axiosInternalInstance.get<Record<string, DashboardResponse>>(
          getDashboardApiUrl()
        );

      const result: Record<string, DashboardTab> = Object.fromEntries(
        Object.entries(data).map(([name, dashboardData]) => {
          const dashboardName = name;
          const value = dashboardData as any;
          const dashboardTabs: DashboardTab =
            normalizeDashboardTabs(value, name, dashboardName) ??
            ({
              [TAB_DEFAULT_BASE_NAME]: { id: generateUniqueUUID(new Set()), content: [] }
            } as DashboardTab);

          // Process MarketApps in each tab's content
          const processedTabs: DashboardTab = Object.fromEntries(
            Object.entries(dashboardTabs).map(([tabName, tabValue]) => {
              const tabEntry = normalizeTabEntry(tabName, tabValue);
              return [
                tabName,
                {
                  ...tabEntry,
                  content: processMarketApps(tabEntry.content)
                }
              ];
            })
          );

          return [dashboardName, processedTabs];
        })
      );

      return result;
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    structuralSharing: false
  });
};

export type { MarketApps } from '@/features/Dashboard/types';
